// Combo System & Skill Chaining
// Inspired by Lineage II's satisfying skill chains and combos
// Bonus damage/effects for completed combos with visual feedback

// SkillId is just a string identifier for skills
export type SkillId = string;

export interface ComboStep {
  skillId: SkillId;
  timeWindow: number; // ms to use this skill after previous
  bonusDamage: number; // multiplier (1.0 = no bonus, 1.5 = 50% bonus)
  bonusEffect?: 'stun' | 'slow' | 'bleed' | 'critical' | 'mana_restore';
}

export interface ComboChain {
  id: string;
  name: string;
  description: string;
  steps: ComboStep[];
  finisherBonus: number; // Bonus for completing the entire chain
  classRestriction?: string; // If set, only this class can use the combo
}

// Warrior combo chains
const WARRIOR_COMBOS: ComboChain[] = [
  {
    id: 'warrior_devastate',
    name: 'Devastation',
    description: 'Slash → Heroic Strike → Execute',
    steps: [
      { skillId: 'slash', timeWindow: 2000, bonusDamage: 1.0 },
      { skillId: 'heroic_strike', timeWindow: 1500, bonusDamage: 1.25 },
      { skillId: 'execute', timeWindow: 1500, bonusDamage: 1.5, bonusEffect: 'critical' },
    ],
    finisherBonus: 2.0,
    classRestriction: 'warrior',
  },
  {
    id: 'warrior_shield_combo',
    name: 'Shield Master',
    description: 'Shield Bash → Slash → Heroic Strike',
    steps: [
      { skillId: 'shield_bash', timeWindow: 2000, bonusDamage: 1.0, bonusEffect: 'stun' },
      { skillId: 'slash', timeWindow: 2000, bonusDamage: 1.3 },
      { skillId: 'heroic_strike', timeWindow: 1500, bonusDamage: 1.5 },
    ],
    finisherBonus: 1.75,
    classRestriction: 'warrior',
  },
  {
    id: 'warrior_fury',
    name: 'Battle Fury',
    description: 'Battle Cry → Whirlwind → Heroic Strike',
    steps: [
      { skillId: 'battle_cry', timeWindow: 3000, bonusDamage: 1.0 },
      { skillId: 'whirlwind', timeWindow: 2000, bonusDamage: 1.4 },
      { skillId: 'heroic_strike', timeWindow: 1500, bonusDamage: 1.6 },
    ],
    finisherBonus: 2.25,
    classRestriction: 'warrior',
  },
];

// Mage combo chains
const MAGE_COMBOS: ComboChain[] = [
  {
    id: 'mage_elemental',
    name: 'Elemental Mastery',
    description: 'Frostbolt → Fireball → Arcane Missiles',
    steps: [
      { skillId: 'frostbolt', timeWindow: 2500, bonusDamage: 1.0, bonusEffect: 'slow' },
      { skillId: 'fireball', timeWindow: 2000, bonusDamage: 1.3 },
      { skillId: 'arcane_missiles', timeWindow: 1500, bonusDamage: 1.5 },
    ],
    finisherBonus: 2.0,
    classRestriction: 'mage',
  },
  {
    id: 'mage_freeze_shatter',
    name: 'Freeze Shatter',
    description: 'Blizzard → Frostbolt → Ice Lance',
    steps: [
      { skillId: 'blizzard', timeWindow: 3000, bonusDamage: 1.0 },
      { skillId: 'frostbolt', timeWindow: 2000, bonusDamage: 1.25, bonusEffect: 'slow' },
      { skillId: 'ice_lance', timeWindow: 1500, bonusDamage: 2.0, bonusEffect: 'critical' },
    ],
    finisherBonus: 2.5,
    classRestriction: 'mage',
  },
];

// Priest combo chains
const PRIEST_COMBOS: ComboChain[] = [
  {
    id: 'priest_holy_fire',
    name: 'Holy Fire',
    description: 'Divine Smite → Holy Light → Divine Smite',
    steps: [
      { skillId: 'divine_smite', timeWindow: 2500, bonusDamage: 1.0 },
      { skillId: 'holy_light', timeWindow: 2000, bonusDamage: 1.0, bonusEffect: 'mana_restore' },
      { skillId: 'divine_smite', timeWindow: 2000, bonusDamage: 1.5 },
    ],
    finisherBonus: 1.75,
    classRestriction: 'priest',
  },
  {
    id: 'priest_shield_combo',
    name: 'Divine Protection',
    description: 'Power Word: Shield → Holy Light → Divine Smite',
    steps: [
      { skillId: 'power_word_shield', timeWindow: 3000, bonusDamage: 1.0 },
      { skillId: 'holy_light', timeWindow: 2500, bonusDamage: 1.2 },
      { skillId: 'divine_smite', timeWindow: 2000, bonusDamage: 1.4 },
    ],
    finisherBonus: 1.5,
    classRestriction: 'priest',
  },
];

// All combo chains
const ALL_COMBOS: ComboChain[] = [...WARRIOR_COMBOS, ...MAGE_COMBOS, ...PRIEST_COMBOS];

export interface ComboState {
  activeChain: ComboChain | null;
  currentStep: number;
  lastSkillTime: number;
  comboCounter: number;
  totalBonusDamage: number;
}

export interface ComboResult {
  isCombo: boolean;
  bonusDamage: number;
  bonusEffect?: 'stun' | 'slow' | 'bleed' | 'critical' | 'mana_restore';
  comboStep: number;
  totalSteps: number;
  isFinisher: boolean;
  comboName?: string;
}

export class ComboSystem {
  private state: ComboState = {
    activeChain: null,
    currentStep: 0,
    lastSkillTime: 0,
    comboCounter: 0,
    totalBonusDamage: 1.0,
  };

  private playerClass: string = 'warrior';
  private availableCombos: ComboChain[] = [];

  // Callbacks for UI updates
  public onComboStart?: (comboName: string, totalSteps: number) => void;
  public onComboProgress?: (step: number, totalSteps: number, bonusDamage: number, nextSkillName?: string) => void;
  public onComboComplete?: (comboName: string, totalBonusDamage: number) => void;
  public onComboBroken?: () => void;

  constructor(playerClass?: string) {
    if (playerClass) {
      this.setPlayerClass(playerClass);
    }
  }

  public setPlayerClass(playerClass: string): void {
    this.playerClass = playerClass.toLowerCase();
    this.availableCombos = ALL_COMBOS.filter(
      combo => !combo.classRestriction || combo.classRestriction === this.playerClass
    );
  }

  public onSkillUsed(skillId: SkillId): ComboResult {
    const now = Date.now();

    // Check if we're continuing an existing combo
    if (this.state.activeChain) {
      const timeSinceLastSkill = now - this.state.lastSkillTime;
      const currentStep = this.state.activeChain.steps[this.state.currentStep];

      // Check if within time window
      if (timeSinceLastSkill <= currentStep.timeWindow) {
        // Check if this is the expected skill
        if (skillId === currentStep.skillId) {
          return this.advanceCombo(now);
        }
      }

      // Combo broken - reset
      this.resetCombo();
      this.onComboBroken?.();
    }

    // Try to start a new combo
    for (const combo of this.availableCombos) {
      if (combo.steps[0].skillId === skillId) {
        return this.startCombo(combo, now);
      }
    }

    // No combo
    return {
      isCombo: false,
      bonusDamage: 1.0,
      comboStep: 0,
      totalSteps: 0,
      isFinisher: false,
    };
  }

  private startCombo(combo: ComboChain, time: number): ComboResult {
    this.state.activeChain = combo;
    this.state.currentStep = 1; // Move to next expected step
    this.state.lastSkillTime = time;
    this.state.comboCounter = 1;
    this.state.totalBonusDamage = combo.steps[0].bonusDamage;

    this.onComboStart?.(combo.name, combo.steps.length);

    return {
      isCombo: true,
      bonusDamage: combo.steps[0].bonusDamage,
      bonusEffect: combo.steps[0].bonusEffect,
      comboStep: 1,
      totalSteps: combo.steps.length,
      isFinisher: false,
      comboName: combo.name,
    };
  }

  private advanceCombo(time: number): ComboResult {
    const combo = this.state.activeChain!;
    const step = combo.steps[this.state.currentStep];

    this.state.currentStep++;
    this.state.lastSkillTime = time;
    this.state.comboCounter++;
    this.state.totalBonusDamage *= step.bonusDamage;

    const isFinisher = this.state.currentStep >= combo.steps.length;

    if (isFinisher) {
      // Apply finisher bonus
      const finalBonus = this.state.totalBonusDamage * combo.finisherBonus;

      this.onComboComplete?.(combo.name, finalBonus);
      this.resetCombo();

      return {
        isCombo: true,
        bonusDamage: finalBonus,
        bonusEffect: step.bonusEffect,
        comboStep: combo.steps.length,
        totalSteps: combo.steps.length,
        isFinisher: true,
        comboName: combo.name,
      };
    }

    // Get next skill name for UI hint
    const nextStep = combo.steps[this.state.currentStep];
    const nextSkillName = nextStep ? nextStep.skillId : undefined;
    this.onComboProgress?.(this.state.currentStep, combo.steps.length, step.bonusDamage, nextSkillName);

    return {
      isCombo: true,
      bonusDamage: step.bonusDamage,
      bonusEffect: step.bonusEffect,
      comboStep: this.state.currentStep,
      totalSteps: combo.steps.length,
      isFinisher: false,
      comboName: combo.name,
    };
  }

  private resetCombo(): void {
    this.state.activeChain = null;
    this.state.currentStep = 0;
    this.state.lastSkillTime = 0;
    this.state.comboCounter = 0;
    this.state.totalBonusDamage = 1.0;
  }

  // Check for combo timeout each frame
  public update(): void {
    if (!this.state.activeChain) return;

    const now = Date.now();
    const timeSinceLastSkill = now - this.state.lastSkillTime;
    const currentStep = this.state.activeChain.steps[this.state.currentStep];

    if (timeSinceLastSkill > currentStep.timeWindow) {
      this.resetCombo();
      this.onComboBroken?.();
    }
  }

  // Get the next expected skill for combo hints
  public getNextComboSkill(): SkillId | null {
    if (!this.state.activeChain) return null;
    if (this.state.currentStep >= this.state.activeChain.steps.length) return null;

    return this.state.activeChain.steps[this.state.currentStep].skillId;
  }

  // Get time remaining in current combo window
  public getComboTimeRemaining(): number {
    if (!this.state.activeChain) return 0;
    if (this.state.currentStep >= this.state.activeChain.steps.length) return 0;

    const currentStep = this.state.activeChain.steps[this.state.currentStep];
    const elapsed = Date.now() - this.state.lastSkillTime;
    return Math.max(0, currentStep.timeWindow - elapsed);
  }

  public getCurrentComboState(): ComboState {
    return { ...this.state };
  }

  public getAvailableCombos(): ComboChain[] {
    return [...this.availableCombos];
  }

  // Get combos that can be started with a specific skill
  public getCombosForSkill(skillId: SkillId): ComboChain[] {
    return this.availableCombos.filter(combo => combo.steps[0].skillId === skillId);
  }
}
